import { FormEvent, ChangeEvent, RefObject } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import ReduxStoreInterface from '../../interfaces/client/ReduxStore'
import NotificationInterface from '../../interfaces/client/Notification'

import {
  resetNotifications,
  removeNotification,
} from '../../actions/notifications'
import updateBooking from '../../actions/bookings/updateBooking'
import { openPassengersToBeDeletedConfirmation } from '../../actions/modals'
import validateDeletePassengers from '../../actions/passengers/validateDeletePassengers'
import {
  enableDeletePassengers,
  disableDeletePassengers,
  setDeletePassengersFromSingleFlight,
  setDeletePassengersFromMultipleFlights,
} from '../../actions/deletePassengers'

import createRef from '../../utils/createRef'
import getFormValuesWithoutRef from '../../utils/getFormValuesWithoutRef'
import { isRemoveInput, buildPassengersFromFormValues } from '../../utils/forms'
import {
  isPassengerUpdated,
  passengerIsNotCheckedIn,
} from '../../utils/passengers'
import {
  getGlobalWarningNotifications,
  getGlobalInfoNotifications,
  getGlobalSuccessNotifications,
  getGlobalErrorNotifications,
} from '../../utils/notifications'

import EditBookingForm from '../../components/EditBookingForm'

interface EditBookingFormPropsInterface {
  flightId: string
  isTOFlight: boolean
}
interface DeleteOptions {
  deletePassengerFromSingleFlight: boolean
}

const isChecked = (element: any) => element.checked
const getPassengerId = (element: any) => element.dataset.passengerId
const formRef: RefObject<HTMLFormElement> = createRef()
const formWrapperRef: RefObject<HTMLDivElement> = createRef()

export const getPassengerFromState = (state: ReduxStoreInterface) => (
  passengerId: string
) => {
  if (state.passengers.byId[passengerId]) {
    return state.passengers.byId[passengerId]
  }

  const passengerIdFound = state.passengers.allIds.find(
    (passengerIdFromAllIds: string) => {
      const passenger = state.passengers.byId[passengerIdFromAllIds]

      const isPassenger = passenger.id === passengerId
      const isInfant =
        passenger.infant &&
        passenger.infant.id &&
        passenger.infant.id === passengerId

      return isPassenger || isInfant ? passenger : null
    }
  )

  if (passengerIdFound) {
    return state.passengers.byId[passengerIdFound]
  }

  return null
}

export const mapStateToProps = (
  state: ReduxStoreInterface,
  { flightId, isTOFlight }: EditBookingFormPropsInterface
) => {
  const notifications = state.notifications.allIds.map(
    (id: string) => state.notifications.byId[id]
  )

  const warningNotifications = getGlobalWarningNotifications(notifications)
  const infoNotifications = getGlobalInfoNotifications(notifications)
  const successNotifications = getGlobalSuccessNotifications(notifications)
  const errorNotifications = getGlobalErrorNotifications(notifications)

  const errors = [...warningNotifications, ...errorNotifications]

  const hasFieldNotification = notifications.find(
    (notification: NotificationInterface) => notification.fieldName
  )

  return {
    flightId,
    isTOFlight,
    formRef,
    formWrapperRef,
    errors,
    notifications: [...infoNotifications, ...successNotifications],
    passengerIds: state.passengers.allIds,
    hasFieldNotification: Boolean(hasFieldNotification),
    isDeletePassengersEnabled: state.deletePassengers.isDeletePassengersEnabled,
    arePassengersSelectedForDeletion: Boolean(
      state.passengersToBeDeleted.passengersSelectedForDeletion.length
    ),
    passengersSSRs: state.passengerSSRs.byId,
  }
}

export const mapDispatchToProps = (dispatch: Dispatch) => ({
  handleSubmit: (event: FormEvent<HTMLFormElement>, flightId: string) => {
    event.preventDefault()

    const formValues = getFormValuesWithoutRef(
      Array.from(event.currentTarget.elements)
    )

    const updatedPassengers = buildPassengersFromFormValues(formValues)
      .filter(isPassengerUpdated)
      .filter(passengerIsNotCheckedIn(flightId))

    if (updatedPassengers.length) {
      dispatch(updateBooking(updatedPassengers, flightId))
    }
  },
  removeNotification: (notification: NotificationInterface) => {
    dispatch(removeNotification(notification.id))
  },
  removeNotifications: () => {
    dispatch(resetNotifications())
  },
  enableDeletePassengers: () => {
    dispatch(enableDeletePassengers())
  },
  disableDeletePassengers: () => {
    dispatch(disableDeletePassengers())
  },
  handleDeletePassengers: (
    event: ChangeEvent<HTMLButtonElement>,
    flightId: string,
    options: DeleteOptions
  ) => {
    event.preventDefault()

    if (!formRef.current) return

    const formElements = formRef.current.elements
    const passengerIdsToBeRemoved = Array.from(formElements)
      .filter(isRemoveInput)
      .filter(isChecked)
      .map(getPassengerId)

    if (!passengerIdsToBeRemoved.length) return

    if (options.deletePassengerFromSingleFlight) {
      dispatch(validateDeletePassengers(passengerIdsToBeRemoved, flightId))
      dispatch(setDeletePassengersFromSingleFlight())
    } else {
      dispatch(validateDeletePassengers(passengerIdsToBeRemoved, flightId))
      dispatch(setDeletePassengersFromMultipleFlights())
    }

    dispatch(openPassengersToBeDeletedConfirmation())
  },
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditBookingForm)
